import React from 'react';
import { Checkbox } from '@mantine/core';

export interface AfaCheckboxProps {
  /** Si está marcado */
  checked?: boolean;
  
  /** Callback cuando cambia el estado */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  /** Label del checkbox */
  label?: React.ReactNode;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Estado indeterminado */
  indeterminate?: boolean;
  
  /** Tamaño del checkbox */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Color del checkbox */
  color?: string;
  
  /** Icono personalizado cuando está marcado */
  icon?: React.FC<any>;
  
  /** Props adicionales para el Checkbox */
  checkboxProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
  
  /** Valor del checkbox para formularios */
  value?: string;
  
  /** Nombre del input para formularios */
  name?: string;
  
  /** ID del input para accesibilidad */
  id?: string;
}

/**
 * Wrapper del Checkbox de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico
 * <AfaCheckbox
 *   label="Accepto els termes i condicions"
 * />
 * 
 * // Con descripción
 * <AfaCheckbox
 *   label="Notificacions per email"
 *   description="Rebràs emails sobre actualitzacions"
 * />
 * 
 * // Controlado
 * const [checked, setChecked] = useState(false);
 * <AfaCheckbox
 *   checked={checked}
 *   onChange={(e) => setChecked(e.target.checked)}
 *   label="Opció controlada"
 * />
 */
export function AfaCheckbox({
  checked,
  onChange,
  label,
  description,
  error,
  disabled = false,
  indeterminate = false,
  size = 'md',
  radius = 'sm',
  color,
  icon,
  checkboxProps,
  styles,
  className,
  value,
  name,
  id,
}: AfaCheckboxProps) {

  return (
    <Checkbox
      checked={checked}
      onChange={onChange}
      label={label}
      description={description}
      error={error}
      disabled={disabled}
      indeterminate={indeterminate}
      size={size}
      radius={radius}
      color={color || 'brand'}
      icon={icon}
      value={value}
      name={name}
      id={id}
      styles={{
        input: {
          '&:checked': {
            backgroundColor: 'var(--mantine-color-brand-6)',
            borderColor: 'var(--mantine-color-brand-6)',
          },
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: `0 0 0 2px var(--mantine-color-brand-1)`,
          },
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 400,
          fontSize: 'var(--mantine-font-size-md)',
        },
        description: {
          color: 'var(--mantine-color-neutral-6)',
        },
        error: {
          color: 'var(--mantine-color-red-6)',
        },
        ...styles,
      }}
      {...(className && { className })}
      {...checkboxProps}
    />
  );
}